import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type { Client, CrmData, Task, AllowedEmail } from '../types/crm';
import { fetchPortalMembersByKind } from './accessManagement';
import { normalizeIntelProfileRegistry } from './acraRegistry';
import { getActivePortalKind } from './supabase';

function normalizeClientRow(row: Client): Client {
  const intelProfile = normalizeIntelProfileRegistry(row.intel_profile_json);
  if (intelProfile === row.intel_profile_json) return row;
  return { ...row, intel_profile_json: intelProfile };
}

export type CrmDataSlice =
  | 'projects'
  | 'clients'
  | 'contacts'
  | 'tasks'
  | 'users'
  | 'updates'
  | 'meetingMinutes'
  | 'transferRequests'
  | 'notifications'
  | 'activityLogs';

export type CrmRefreshOptions = {
  silent?: boolean;
  slices?: CrmDataSlice[];
};

/** First paint — Dashboard / Tasks need these quickly. */
export const CRM_BLOCKING_SLICES: CrmDataSlice[] = [
  'projects',
  'clients',
  'contacts',
  'tasks',
  'users',
  'notifications',
];

/** Still core CRM, but safe to load after the UI is interactive. */
export const CRM_DEFERRED_CORE_SLICES: CrmDataSlice[] = ['updates', 'transferRequests'];

/** @deprecated Use CRM_BLOCKING_SLICES + CRM_DEFERRED_CORE_SLICES */
export const CRM_CORE_SLICES: CrmDataSlice[] = [...CRM_BLOCKING_SLICES, ...CRM_DEFERRED_CORE_SLICES];

/** Account detail tables — loaded in the background after core. */
export const CRM_AUXILIARY_SLICES: CrmDataSlice[] = ['meetingMinutes', 'activityLogs'];

export const CRM_TASK_SAVE_SLICES: CrmDataSlice[] = ['tasks'];

/** Keep recently saved tasks visible through realtime / slice refresh lag. */
export const CRM_TASK_PATCH_TTL_MS = 12_000;

export function mergeTasksFromServer(
  local: Task[],
  remote: Task[],
  preserveLocalIds?: ReadonlySet<string>,
): Task[] {
  if (!preserveLocalIds?.size) return remote;
  const remoteIds = new Set(remote.map((t) => String(t.id)));
  const extras = local.filter((t) => preserveLocalIds.has(String(t.id)) && !remoteIds.has(String(t.id)));
  if (extras.length === 0) return remote;
  const byId = new Map<string, Task>();
  for (const task of [...extras, ...remote]) {
    byId.set(String(task.id), task);
  }
  return [...byId.values()].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
}

export function allCrmDataSlices(): CrmDataSlice[] {
  return [...CRM_CORE_SLICES, ...CRM_AUXILIARY_SLICES];
}

export function mergeTaskIntoList(tasks: Task[], task: Task): Task[] {
  const id = String(task.id);
  const idx = tasks.findIndex((row) => String(row.id) === id);
  if (idx >= 0) {
    return tasks.map((row, i) => (i === idx ? task : row));
  }
  return [task, ...tasks];
}

export function markTaskDoneInList(tasks: Task[], taskId: string): Task[] {
  const id = String(taskId);
  return tasks.map((row) => (String(row.id) === id ? { ...row, status: 'Done' } : row));
}

export function mergeClientIntoList(clients: Client[], client: Client): Client[] {
  const id = String(client.id);
  const idx = clients.findIndex((row) => String(row.id) === id);
  if (idx >= 0) {
    return clients.map((row, i) => (i === idx ? client : row));
  }
  return [...clients, client].sort((a, b) => a.company_name.localeCompare(b.company_name));
}

export type CrmSliceFetchResult = {
  partial: Partial<CrmData>;
  rawUsers: AllowedEmail[] | null;
  errors: Partial<Record<CrmDataSlice, PostgrestError | null>>;
};

export async function fetchCrmDataSlices(
  db: SupabaseClient,
  slices: readonly CrmDataSlice[],
  profileId?: string | null,
): Promise<CrmSliceFetchResult> {
  const wanted = new Set(slices);
  const partial: Partial<CrmData> = {};
  const errors: Partial<Record<CrmDataSlice, PostgrestError | null>> = {};
  let rawUsers: AllowedEmail[] | null = null;

  const jobs: Array<PromiseLike<unknown>> = [];

  if (wanted.has('projects')) {
    jobs.push(
      db
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          partial.projects = data ?? [];
          if (error) errors.projects = error;
        }),
    );
  }

  if (wanted.has('clients')) {
    jobs.push(
      db
        .from('clients')
        .select('*')
        .order('company_name')
        .then(({ data, error }) => {
          partial.clients = (data ?? []).map((row) => normalizeClientRow(row as Client));
          if (error) errors.clients = error;
        }),
    );
  }

  if (wanted.has('contacts')) {
    jobs.push(
      db
        .from('contacts')
        .select('*')
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          partial.contacts = data ?? [];
          if (error) errors.contacts = error;
        }),
    );
  }

  if (wanted.has('tasks')) {
    jobs.push(
      db
        .from('tasks')
        .select('*')
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          partial.tasks = data ?? [];
          if (error) errors.tasks = error;
        }),
    );
  }

  if (wanted.has('users')) {
    jobs.push(
      (async () => {
        try {
          const kind = getActivePortalKind() === 'it' ? 'it' : 'crm';
          const rows = await fetchPortalMembersByKind(kind);
          rawUsers = rows.map((row) => ({
            id: row.id,
            email: row.email,
            display_name: row.display_name,
            role: row.role as AllowedEmail['role'],
            username: row.username,
            auth_id: row.auth_id,
            created_at: row.created_at,
          }));
        } catch (err) {
          errors.users = {
            message: err instanceof Error ? err.message : String(err),
            details: '',
            hint: '',
            code: 'PGRST000',
            name: 'PostgrestError',
          } as PostgrestError;
        }
      })(),
    );
  }

  if (wanted.has('updates')) {
    jobs.push(
      db
        .from('project_updates')
        .select('*')
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          partial.updates = data ?? [];
          if (error) errors.updates = error;
        }),
    );
  }

  if (wanted.has('meetingMinutes')) {
    jobs.push(
      db
        .from('account_meeting_minutes')
        .select('*')
        .order('meeting_date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(1000)
        .then(({ data, error }) => {
          partial.meetingMinutes = data ?? [];
          if (error) errors.meetingMinutes = error;
        }),
    );
  }

  if (wanted.has('transferRequests')) {
    jobs.push(
      db
        .from('account_transfer_requests')
        .select('*')
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          partial.transferRequests = data ?? [];
          if (error) errors.transferRequests = error;
        }),
    );
  }

  if (wanted.has('notifications')) {
    jobs.push(
      (profileId
        ? db
            .from('crm_notifications')
            .select('*')
            .eq('user_id', profileId)
            .order('created_at', { ascending: false })
            .limit(80)
        : Promise.resolve({ data: [], error: null })
      ).then(({ data, error }) => {
        partial.notifications = data ?? [];
        if (error) errors.notifications = error;
      }),
    );
  }

  if (wanted.has('activityLogs')) {
    jobs.push(
      db
        .from('account_activity_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1000)
        .then(({ data, error }) => {
          partial.activityLogs = data ?? [];
          if (error) errors.activityLogs = error;
        }),
    );
  }

  await Promise.all(jobs);
  return { partial, rawUsers, errors };
}

export function sliceErrorLabel(slice: CrmDataSlice): string {
  switch (slice) {
    case 'users':
      return 'portal_access (team list)';
    case 'updates':
      return 'project_updates';
    case 'meetingMinutes':
      return 'account_meeting_minutes';
    case 'transferRequests':
      return 'account_transfer_requests';
    case 'notifications':
      return 'crm_notifications';
    case 'activityLogs':
      return 'account_activity_log';
    default:
      return slice;
  }
}

export function applyCrmSlicePartial(prev: CrmData, partial: Partial<CrmData>): CrmData {
  return {
    ...prev,
    ...(partial.projects !== undefined ? { projects: partial.projects } : {}),
    ...(partial.clients !== undefined ? { clients: partial.clients } : {}),
    ...(partial.contacts !== undefined ? { contacts: partial.contacts } : {}),
    ...(partial.tasks !== undefined ? { tasks: partial.tasks } : {}),
    ...(partial.users !== undefined ? { users: partial.users } : {}),
    ...(partial.updates !== undefined ? { updates: partial.updates } : {}),
    ...(partial.meetingMinutes !== undefined ? { meetingMinutes: partial.meetingMinutes } : {}),
    ...(partial.transferRequests !== undefined ? { transferRequests: partial.transferRequests } : {}),
    ...(partial.notifications !== undefined ? { notifications: partial.notifications } : {}),
    ...(partial.activityLogs !== undefined ? { activityLogs: partial.activityLogs } : {}),
  };
}
